import { Global, css, useTheme } from '@emotion/react';

function GlobalStyles() {
  const theme = useTheme();
  const isDark = theme.mode === 'dark';

  return (
    <Global
      styles={css`
        /* ---------- Typography imports ---------- */
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');

        /* ---------- CSS variables from the JS theme ---------- */
        :root {
          --color-bg: ${theme.background};
          --color-text: ${theme.text};
          --color-accent: ${theme.accent ?? '#c8102e'};
          --color-muted: ${theme.muted ?? '#8a8a8a'};
          --color-line: ${theme.line ?? 'rgba(12,12,12,0.12)'};
          --radius: ${theme.radius ?? '0px'};
          --shadow-sm: ${theme.shadowSm ?? '0 2px 6px rgba(0,0,0,.06)'};
          --shadow-md: ${theme.shadowMd ?? '0 4px 12px rgba(0,0,0,.10)'};
        }

        /* ---------- Base / reset ---------- */
        *, *::before, *::after { box-sizing: border-box; }
        html, body { height: 100%; }
        html {
          /* better scrolling + honors prefers-reduced-motion below */
          scroll-behavior: smooth;
          -webkit-text-size-adjust: 100%;
        }
        body {
          margin: 0;
          padding: 0;
          font-family: ${theme.font ?? "'Helvetica Neue', system-ui, -apple-system, Segoe UI, Roboto, sans-serif"};
          font-size: ${theme.baseFontSize ?? '15px'};
          line-height: ${theme.lineHeight ?? 1.5};
          color: var(--color-text);
          background: var(--color-bg);
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
          transition: background-color .3s ease, color .3s ease;
          /* Tells the browser which palette to use for built-in UI */
          color-scheme: ${isDark ? 'dark' : 'light'};
        }
        h1,h2,h3,h4,h5,h6 {
          font-family: ${theme.headingFont ?? theme.font ?? "'Helvetica Neue', system-ui, -apple-system, Segoe UI, Roboto, sans-serif"};
          letter-spacing: -0.02em;
        }

        /* ---------- Typography defaults ---------- */
        h1,h2,h3,h4,h5,h6 { margin: 0 0 .5rem; line-height: 1.25; }
        p { margin: 0 0 .75rem; }
        small { font-size: 0.875em; color: var(--color-muted); }
        strong { font-weight: 700; }

        /* ---------- Links ---------- */
        a {
          color: var(--color-accent);
          text-decoration: none;
          position: relative;
        }
        a::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -2px;
          width: 100%;
          height: 1px;
          background: currentColor;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform .2s ease;
        }
        a:hover::after { transform: scaleX(1); }
        a:focus-visible {
          outline: 2px solid var(--color-accent);
          outline-offset: 2px;
          border-radius: 0;
          text-decoration: none;
        }

        /* ---------- Media ---------- */
        img, picture, video, canvas, svg { display: block; max-width: 100%; }
        img { height: auto; }

        /* ---------- Forms ---------- */
        input, button, textarea, select {
          font: inherit;
          color: inherit;
        }
        ::placeholder { color: ${isDark ? '#a8a8a8' : '#8f8f8f'}; opacity: .95; }

        /* ---------- Selection ---------- */
        ::selection {
          background: ${theme.selectionBg ?? 'rgba(231,87,16,.22)'};
          color: ${theme.selectionText ?? (isDark ? '#fff' : '#141415')};
        }

        /* ---------- Layout helpers ---------- */
        section {
          padding: ${theme.sectionPadding ?? '5.25rem 1.6rem'};
          max-width: ${theme.maxWidth ?? '1180px'};
          margin: 0 auto;
        }
        @media (max-width: 768px) {
          section { padding: ${theme.sectionPaddingSm ?? '3.5rem 1.3rem'}; }
        }

        /* ---------- Motion & accessibility ---------- */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-delay: -1ms !important;
            animation-duration: 1ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0ms !important;
            scroll-behavior: auto !important;
          }
        }

        /* ---------- Scrollbar (WebKit) ---------- */
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-thumb {
          background: ${isDark ? '#3a3a3a' : '#d8d8d8'};
          border-radius: 0;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? '#505050' : '#c8c8c8'};
        }
      `}
    />
  );
}

export default GlobalStyles;
